import React from 'react';
import { Modal, App } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';

export interface DeleteConfirmModalProps {
  /**
   * 要删除的项目名称
   */
  itemName: string;
  /**
   * 删除确认的标题，默认为"确认删除"
   */
  title?: string;
  /**
   * 删除确认的内容，如果不提供则使用默认格式
   */
  content?: string;
  /**
   * 确认删除的回调函数
   */
  onConfirm: () => void | Promise<void>;
  /**
   * 取消删除的回调函数
   */
  onCancel?: () => void;
  /**
   * 是否显示危险样式的确认按钮
   */
  danger?: boolean;
  /**
   * 确认按钮的文本
   */
  okText?: string;
  /**
   * 取消按钮的文本
   */
  cancelText?: string;
  /**
   * 是否显示图标
   */
  showIcon?: boolean;
  /**
   * 自定义图标
   */
  icon?: React.ReactNode;
}

/**
 * 通用的删除确认弹窗组件
 * 提供统一的删除确认UI和交互逻辑
 */
const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({
  itemName,
  title = '确认删除',
  content,
  onConfirm,
  onCancel,
  danger = true,
  okText = '删除',
  cancelText = '取消',
  showIcon = true,
  icon,
}) => {
  const { modal } = App.useApp();

  const handleConfirm = () => {
    modal.confirm({
      title,
      content: content || `确定要删除${itemName}吗？此操作不可逆。`,
      icon: showIcon ? (icon || <ExclamationCircleOutlined />) : undefined,
      okText,
      cancelText,
      okType: danger ? 'danger' : 'primary',
      onOk: onConfirm,
      onCancel,
    });
  };

  return null; // 这个组件不渲染任何内容，只是提供方法
};

/**
 * 删除确认Hook
 * 提供更简洁的使用方式
 */
export const useDeleteConfirm = () => {
  const { modal } = App.useApp();

  const showDeleteConfirm = (options: Omit<DeleteConfirmModalProps, 'onConfirm'> & {
    onConfirm: () => void | Promise<void>;
  }) => {
    const {
      itemName,
      title = '确认删除',
      content,
      onConfirm,
      onCancel,
      danger = true,
      okText = '删除',
      cancelText = '取消',
      showIcon = true,
      icon,
    } = options;

    modal.confirm({
      title,
      content: content || `确定要删除${itemName}吗？此操作不可逆。`,
      icon: showIcon ? (icon || <ExclamationCircleOutlined />) : undefined,
      okText,
      cancelText,
      okType: danger ? 'danger' : 'primary',
      onOk: onConfirm,
      onCancel,
    });
  };

  return { showDeleteConfirm };
};

export default DeleteConfirmModal;
